import React from 'react';
import { FiX } from 'react-icons/fi';
import { formatSingaporeDateDmyFull, toSingaporeDateInputValue } from '../utils/singaporeTime';

function sameCalendarDay(a, b) {
    if (a == null || b == null || a === '' || b === '') return false;
    const sgA = toSingaporeDateInputValue(a);
    const sgB = toSingaporeDateInputValue(b);
    if (sgA && sgB && sgA === sgB) return true;
    const da = new Date(a);
    const db = new Date(b);
    if (!Number.isFinite(da.getTime()) || !Number.isFinite(db.getTime())) return false;
    return da.toDateString() === db.toDateString();
}

function parseSingaporeYmdParts(value) {
    const ymd = toSingaporeDateInputValue(value);
    if (!ymd) return null;
    const [year, month, day] = ymd.split('-').map(Number);
    if (!year || !month || !day) return null;
    return { year, month, day, ymd };
}

function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function calendarDiffForward(start, end) {
    let years = end.year - start.year;
    let months = end.month - start.month;
    let days = end.day - start.day;

    if (days < 0) {
        months--;
        const prevMonth = end.month === 1 ? 12 : end.month - 1;
        const prevYear = end.month === 1 ? end.year - 1 : end.year;
        days += daysInMonth(prevYear, prevMonth);
    }

    if (months < 0) {
        years--;
        months += 12;
    }

    return { years, months, days };
}

function calculateTimeDifference(paymentDate, renewalDate) {
    const start = parseSingaporeYmdParts(paymentDate);
    const end = parseSingaporeYmdParts(renewalDate);
    if (!start || !end) return null;

    const inverted = end.ymd < start.ymd;
    const from = inverted ? end : start;
    const to = inverted ? start : end;
    return { ...calendarDiffForward(from, to), inverted };
}

function formatTimeDisplay(timeDiff) {
    if (!timeDiff) return '';
    const parts = [];
    if (timeDiff.years > 0) {
        parts.push(`${timeDiff.years} year${timeDiff.years > 1 ? 's' : ''}`);
    }
    if (timeDiff.months > 0) {
        parts.push(`${timeDiff.months} month${timeDiff.months > 1 ? 's' : ''}`);
    }
    if (timeDiff.days > 0) {
        parts.push(`${timeDiff.days} day${timeDiff.days > 1 ? 's' : ''}`);
    }
    const label = parts.join(' ') || '0 days';
    return timeDiff.inverted ? `-${label}` : label;
}

const PaymentConfirmationModal = ({ 
    isOpen, 
    onConfirm, 
    onCancel, 
    paymentData,
    memberData,
    groupData 
}) => {
    if (!isOpen) return null;

    const timeDiff = paymentData.renewalDate
        ? calculateTimeDifference(paymentData.paymentDate, paymentData.renewalDate)
        : null;
    const timeDisplay = timeDiff ? formatTimeDisplay(timeDiff) : '';
    const isRenewalType = ['individual', 'group', 'add_on'].includes(paymentData.paymentType);
    const isShortDuration = Boolean(
        isRenewalType
        && timeDiff
        && !timeDiff.inverted
        && timeDiff.years === 0
        && timeDiff.months === 0
        && timeDiff.days < 30
    );
    const isInvertedDuration = Boolean(isRenewalType && timeDiff?.inverted);
    const shortDurationLabel = timeDiff
        ? `${timeDiff.days} Day${timeDiff.days === 1 ? '' : 's'}`
        : '';
    const isFourHundredOverTwoMonths = Boolean(
        paymentData.paymentType === 'individual'
        && Number(paymentData.paymentAmount) === 400
        && timeDiff
        && !timeDiff.inverted
        && (
            timeDiff.years > 0
            || timeDiff.months > 2
            || (timeDiff.months === 2 && timeDiff.days > 0)
        )
    );
    const isOverThreeMonthsThreeDays = Boolean(
        isRenewalType
        && timeDiff
        && !timeDiff.inverted
        && (
            timeDiff.years > 0
            || timeDiff.months > 3
            || (timeDiff.months === 3 && timeDiff.days > 3)
        )
    );

    const isGroupPayment = paymentData.paymentType === 'group' || paymentData.paymentType === 'add_on';
    const paymentDateLabel = formatSingaporeDateDmyFull(paymentData.paymentDate);
    const participants = groupData?.groupMembers?.participants || [];
    const hasJoinOnPaymentDate = participants.some((p) => sameCalendarDay(p.joinDate, paymentData.paymentDate));
    const missingParticipantJoin = isGroupPayment && !hasJoinOnPaymentDate;

    const handleConfirm = () => {
        if (missingParticipantJoin) {
            const ok = window.confirm(`No Participant join at ${paymentDateLabel}`);
            if (!ok) return;
        }
        if (isShortDuration) {
            const ok = window.confirm(`ONLY ${shortDurationLabel}?`);
            if (!ok) return;
        }
        if (isInvertedDuration) {
            const ok = window.confirm(`ONLY ${timeDisplay}?`);
            if (!ok) return;
        }
        if (isFourHundredOverTwoMonths) {
            const ok = window.confirm(`Confirm ${timeDisplay}?  $400 should be 1 month`);
            if (!ok) return;
        }
        if (isOverThreeMonthsThreeDays) {
            const ok = window.confirm(`Confirm ${timeDisplay}?`);
            if (!ok) return;
        }
        onConfirm();
    };

    // Get member/group name
    const getMemberOrGroupName = () => {
        if ((paymentData.paymentType === 'individual' || paymentData.paymentType === 'misc_fees') && memberData) {
            return memberData.fullName;
        } else if ((paymentData.paymentType === 'group' || paymentData.paymentType === 'add_on') && groupData) {
            return groupData.groupName;
        }
        return 'N/A';
    };

    // Get payment type display
    const getPaymentTypeDisplay = () => {
        if (paymentData.paymentType === 'individual') {
            return 'Individual';
        } else if (paymentData.paymentType === 'group') {
            return 'Group';
        } else if (paymentData.paymentType === 'add_on') {
            return 'Add-on';
        } else if (paymentData.paymentType === 'misc_fees') {
            return 'Misc Fees';
        }
        return paymentData.paymentType;
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Confirm Payment Creation</h3>
                    <button 
                        onClick={onCancel} 
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                        <h4 className="font-semibold text-gray-900 mb-3">Payment Summary</h4>
                        
                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <span className="text-gray-600">God Member:</span>
                                <span className="font-medium">{getMemberOrGroupName()}</span>
                            </div>
                            
                            <div className="flex justify-between">
                                <span className="text-gray-600">Type:</span>
                                <span className="font-medium">{getPaymentTypeDisplay()}</span>
                            </div>
                            
                            <div className="flex justify-between">
                                <span className="text-gray-600">Amount:</span>
                                <span className="font-medium">${parseFloat(paymentData.paymentAmount).toFixed(2)}</span>
                            </div>
                            
                            <div className="flex justify-between">
                                <span className="text-gray-600">Payment Date:</span>
                                <span className="font-medium">{paymentDateLabel}</span>
                            </div>
                            
                            {paymentData.renewalDate && (
                            <>
                            <div className="flex justify-between">
                                <span className="text-gray-600">Renewal Date:</span>
                                <span className="font-medium">{new Date(paymentData.renewalDate).toLocaleDateString()}</span>
                            </div>
                            
                            <div className="flex justify-between border-t pt-2">
                                <span className="text-gray-600">Duration:</span>
                                <span className={`font-medium ${isShortDuration || timeDiff?.inverted ? 'text-red-600' : 'text-blue-600'}`}>
                                    {timeDisplay || '0 days'}
                                </span>
                            </div>
                            </>
                            )}
                        </div>
                    </div>

                    {paymentData.paymentRemarks && (
                        <div className="bg-yellow-50 p-3 rounded-md">
                            <span className="text-gray-600">Remarks:</span>
                            <p className="text-gray-800 mt-1">{paymentData.paymentRemarks}</p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Confirm Payment
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentConfirmationModal;
